/* ============================================================================
 * GFI Nexus — User profile + role helpers.
 *
 * Single-cache, single-flight wrapper around the user_profiles table.
 *
 *   getCurrentProfile()  → resolved profile row for the logged-in user
 *                          (null when signed out OR when the user_profiles
 *                          row doesn't exist yet — both treated as "no
 *                          profile data" by callers).
 *   getCurrentRole()     → 'admin' | 'shipper' | null
 *   isAdmin() / isShipper() — boolean convenience.
 *   clearProfileCache()  → call on logout so the next sign-in re-fetches.
 *
 * Why a module-level cache: every render of App.js asks for the role; we
 * don't want to slam Supabase 30× per page. TTL is short enough (90s) that
 * an admin promoting/demoting another user sees their change quickly
 * without a manual refresh.
 *
 * Resilience: if the user_profiles table doesn't exist yet (admin hasn't
 * run the SQL migration), getCurrentProfile returns null and the caller
 * falls back to admin so the existing app keeps working. This is the
 * deliberate "broken means admin" fallback — better UX than locking
 * Arturo out of his own system during the migration window.
 * ============================================================================ */

import { supabase } from './supabaseClient';

const CACHE_TTL_MS = 90 * 1000;
let cached = null;       // { profile, fetchedAt, uid }
let inflight = null;     // dedupe concurrent calls

export async function getCurrentProfile(force = false) {
  // Reuse cache if fresh and matches current user.
  if (!force && cached && (Date.now() - cached.fetchedAt) < CACHE_TTL_MS) {
    return cached.profile;
  }
  if (inflight) return inflight;
  inflight = (async () => {
    try {
      const sess = await supabase.auth.getSession();
      const uid = sess && sess.data && sess.data.session && sess.data.session.user && sess.data.session.user.id;
      if (!uid) {
        cached = { profile: null, fetchedAt: Date.now(), uid: null };
        return null;
      }
      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', uid)
        .maybeSingle();
      if (error) {
        // Table missing / RLS denied / network — treat as "no profile data".
        // Caller decides the fallback (admin until proven otherwise).
        cached = { profile: null, fetchedAt: Date.now(), uid: uid };
        return null;
      }
      cached = { profile: data || null, fetchedAt: Date.now(), uid: uid };
      return cached.profile;
    } finally {
      inflight = null;
    }
  })();
  return inflight;
}

export async function getCurrentRole() {
  const p = await getCurrentProfile();
  return p ? p.role : null;
}

export async function isAdmin() {
  const r = await getCurrentRole();
  return r === 'admin';
}

export async function isShipper() {
  const r = await getCurrentRole();
  return r === 'shipper';
}

export function clearProfileCache() {
  cached = null;
}

/* ============================================================================
 * Re-verify the current user's password without disturbing their session.
 *
 * Used by privileged actions (e.g. "Reopen audit") that need a fresh
 * proof-of-identity even though the user is already signed in. We
 * spin a one-off sandbox Supabase client with persistSession=false so
 * the verification sign-in never overwrites the main app session, and
 * sign that sandbox out immediately whether the attempt succeeded or
 * failed.
 *
 * Returns true on a valid password, false on auth failure. Throws
 * only on unexpected wiring problems (missing env vars, network
 * errors) — caller decides how to surface those.
 * ============================================================================ */
export async function verifyCurrentUserPassword(password) {
  if (typeof password !== 'string' || password.length === 0) return false;
  const sess = await supabase.auth.getSession();
  const email = sess && sess.data && sess.data.session && sess.data.session.user && sess.data.session.user.email;
  if (!email) return false;
  const { createClient } = await import('@supabase/supabase-js');
  const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
  const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;
  if (!supabaseUrl || !supabaseAnonKey) throw new Error('Missing Supabase env vars');
  const sandbox = createClient(supabaseUrl, supabaseAnonKey, {
    auth: { persistSession: false, autoRefreshToken: false, detectSessionInUrl: false },
  });
  try {
    const res = await sandbox.auth.signInWithPassword({ email: email, password: password });
    /* Sign the sandbox out unconditionally so the captured tokens
       don't sit in memory. */
    try { await sandbox.auth.signOut(); } catch (e) { /* ignore */ }
    return !res.error;
  } catch (e) {
    try { await sandbox.auth.signOut(); } catch (e2) { /* ignore */ }
    return false;
  }
}

/* ============================================================================
 * Admin-side helpers: list, create, update, deactivate users.
 * RLS gates everything — non-admin callers will get empty results / errors.
 * ============================================================================ */

export async function listAllProfiles() {
  const { data, error } = await supabase
    .from('user_profiles')
    .select('*')
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data || [];
}

/* Update a profile row. Privileged columns (role, supplier_id, is_active)
   silently fail for non-admins because the DB trigger raises — surface
   the error to the caller via throw. */
export async function updateProfile(id, patch) {
  const { error } = await supabase
    .from('user_profiles')
    .update(Object.assign({}, patch, { updated_at: new Date().toISOString() }))
    .eq('id', id);
  if (error) throw error;
  // Invalidate cache so the next read picks up our change.
  if (cached && cached.uid === id) clearProfileCache();
}

/* Toggle a user's is_active flag. Admin-only via the DB trigger. */
export async function setUserActive(id, active) {
  return updateProfile(id, { is_active: !!active });
}

/* Create a user via signUp on a sandboxed Supabase client.
 *
 * Why a sandbox client: supabase.auth.signUp swaps the active session to
 * the NEW user, which would log the admin out. We spin a one-off client
 * with persistSession=false, sign up there, sign that client out, and
 * the admin's main session never moves.
 *
 * The on_auth_user_created DB trigger creates the user_profiles row
 * automatically. After signUp we PATCH that row with the role +
 * supplier_id + phone fields the admin filled in — this PATCH happens
 * through the main client, so it runs under the admin's RLS context
 * and the up_update_admin policy allows it.
 *
 * Returns the new user's id.
 */
export async function adminCreateUser({
  email, password, firstName, lastName, role,
  supplierId, phoneCountryCode, phoneAreaCode, phoneNumber, avatarUrl,
}) {
  // Lazy-load createClient so the bundle doesn't pull a second copy if
  // this admin tool never opens.
  const { createClient } = await import('@supabase/supabase-js');
  const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
  const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;
  if (!supabaseUrl || !supabaseAnonKey) {
    throw new Error('Missing Supabase env vars');
  }
  const sandbox = createClient(supabaseUrl, supabaseAnonKey, {
    auth: { persistSession: false, autoRefreshToken: false, detectSessionInUrl: false },
  });

  // Pass first/last name in raw_user_meta_data so the handle_new_user
  // trigger seeds the user_profiles row with them.
  const signUpRes = await sandbox.auth.signUp({
    email: email,
    password: password,
    options: {
      data: { first_name: firstName || '', last_name: lastName || '' },
    },
  });
  if (signUpRes.error) {
    try { await sandbox.auth.signOut(); } catch (e) { /* ignore */ }
    throw signUpRes.error;
  }
  const newUserId = signUpRes.data && signUpRes.data.user && signUpRes.data.user.id;
  // Sign the sandbox client out immediately so the captured tokens
  // can't leak across renders.
  try { await sandbox.auth.signOut(); } catch (e) { /* ignore */ }
  if (!newUserId) {
    throw new Error('Sign-up returned no user id (email confirmation may be required)');
  }

  // Now patch the auto-created profile with the admin-supplied fields.
  // The main client is the admin session, so RLS lets us write any row.
  const patch = {
    role: role,
    supplier_id: supplierId || null,
    first_name: firstName || '',
    last_name: lastName || '',
    phone_country_code: phoneCountryCode || null,
    phone_area_code: phoneAreaCode || null,
    phone_number: phoneNumber || null,
    avatar_url: avatarUrl || null,
    updated_at: new Date().toISOString(),
  };
  const { error: updErr } = await supabase
    .from('user_profiles')
    .update(patch)
    .eq('id', newUserId);
  if (updErr) {
    // Profile row exists (trigger created it) but we couldn't patch.
    // Surface the failure so the admin can retry from the Edit view.
    throw updErr;
  }
  return newUserId;
}
